/// <reference types="google.maps" />
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useEffect, useRef, useState } from 'react';
import type { Job } from '../types';

const loaderCache = new Map<string, Promise<void>>();
const DEFAULT_CENTER = { lat: 36.06462365003745, lng: -86.91217636951033 };
const DEFAULT_ZOOM = 10;

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (!apiKey) {
    return Promise.reject(new Error('Missing Google Maps API key'));
  }

  const cached = loaderCache.get(apiKey);
  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    if ((window as Window & typeof globalThis & { google?: typeof google }).google?.maps) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps-loader]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker&v=weekly`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  loaderCache.set(apiKey, promise);
  return promise;
}

function buildInfoWindowContent(job: Job): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'map-info-window';

  const title = document.createElement('h3');
  title.textContent = job.summary;
  wrapper.appendChild(title);

  const address = document.createElement('p');
  address.textContent = job.address;
  wrapper.appendChild(address);

  const link = document.createElement('a');
  link.href = job.url;
  link.target = '_blank';
  link.rel = 'noreferrer';
  link.textContent = 'Open Jira ticket';
  wrapper.appendChild(link);

  return wrapper;
}

export interface MapViewProps {
  apiKey: string;
  jobs: Job[];
  activeJobKey: string | null;
  onMarkerSelect: (jobKey: string) => void;
}

export default function MapView({ apiKey, jobs, activeJobKey, onMarkerSelect }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const clusterRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const markerByKeyRef = useRef(new Map<string, google.maps.Marker>());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then(() => {
        if (cancelled || !containerRef.current || mapRef.current) {
          return;
        }

        mapRef.current = new google.maps.Map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: import.meta.env.VITE_GOOGLE_MAP_ID || undefined,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        });

        infoWindowRef.current = new google.maps.InfoWindow();
        setMapReady(true);
      })
      .catch((error) => {
        console.error(error);
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !(window as Window & typeof globalThis & { google?: typeof google }).google?.maps) {
      return;
    }

    clusterRef.current?.clearMarkers();
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
    markerByKeyRef.current = new Map();

    if (!jobs.length) {
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(DEFAULT_ZOOM);
      infoWindowRef.current?.close();
      return;
    }

    const markers = jobs.map((job) => {
      const marker = new google.maps.Marker({
        position: { lat: job.lat as number, lng: job.lng as number },
        title: job.summary,
      });

      marker.addListener('click', () => {
        onMarkerSelect(job.key);
      });

      markerByKeyRef.current.set(job.key, marker);
      return marker;
    });

    markersRef.current = markers;
    clusterRef.current = new MarkerClusterer({ map: mapRef.current, markers });
  }, [jobs, mapReady, onMarkerSelect]);

  useEffect(() => {
    if (!mapReady || !activeJobKey || !mapRef.current || !infoWindowRef.current) {
      return;
    }

    const marker = markerByKeyRef.current.get(activeJobKey);
    const job = jobs.find((item) => item.key === activeJobKey);
    if (!marker || !job) {
      infoWindowRef.current.close();
      return;
    }

    infoWindowRef.current.setContent(buildInfoWindowContent(job));
    infoWindowRef.current.open({ map: mapRef.current, anchor: marker });

    const position = marker.getPosition();
    if (position) {
      mapRef.current.panTo(position);
    }
  }, [activeJobKey, jobs, mapReady]);

  return <div id="map" ref={containerRef} />;
}
